import { useState, type ReactNode } from 'react';
import { siCurl, siNodedotjs, siPython } from 'simple-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { PencilEdit01Icon } from '@hugeicons/core-free-icons';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { CopyButton } from '../../components/CopyButton';
import { CodeHighlight } from '../../components/CodeHighlight';
import { buildSnippets, type SnippetLang } from '../../lib/requestSnippets';
import { cn } from '../../lib/utils';
import { SnippetActions, type SnippetKeySource } from './SnippetActions';
import type { DiscoveryOperation } from '../../data/discovery';
import { m } from '../../paraglide/messages';

const LANG_ICON: Record<SnippetLang, string> = {
  curl: siCurl.path,
  node: siNodedotjs.path,
  python: siPython.path,
};

/** The official brand glyph for a snippet language, tinting with the tab text. */
const LangGlyph = ({ lang }: { lang: SnippetLang }) => (
  <svg viewBox="0 0 24 24" width={13} height={13} fill="currentColor" aria-hidden className="shrink-0">
    <path d={LANG_ICON[lang]} />
  </svg>
);

export interface RequestSnippetsProps {
  baseUrl: string;
  sku: string;
  operation: DiscoveryOperation;
  input: Record<string, unknown>;
  /** Human name of what the SKU returns, e.g. "Instagram Profile". Feeds the
   *  agent prompt in the actions row; the SKU alone carries it when absent. */
  title?: string;
  /** Renders the agent handoff row: open this request in a coding agent, show
   *  the prompt, and reach a key. Opt-in so a surface that already says this
   *  elsewhere on the page does not say it twice. */
  agentActions?: boolean;
  /** Where that row sends a reader who has no key in the snippet. */
  keySource?: SnippetKeySource;
  /** When set, the real key is inlined into the snippets instead of the
   *  `$ANYAPI_KEY` placeholder (onboarding's runnable first call). */
  apiKey?: string;
  /** When set, an "Input" tab (selected by default) is shown before the
   *  language tabs, rendering this node (the editable form). */
  inputTab?: ReactNode;
  /** Controls rendered in a footer below the body (e.g. a Run button + bot
   *  check), visible from every tab. */
  footer?: ReactNode;
}

const LANGS: { id: SnippetLang; label: string }[] = [
  { id: 'python', label: 'Python' },
  { id: 'node', label: 'Node.js' },
  { id: 'curl', label: 'cURL' },
];

type SnippetTab = 'input' | SnippetLang;

/**
 * The current request rendered as copy-paste code in Python, Node.js, and cURL.
 * Updates live as the form changes, so what you run is exactly what you'd ship.
 * Optionally fronted by an "Input" tab (the editable form) and a footer (run
 * controls), so one card holds the form, the snippets, and the action.
 */
export const RequestSnippets = ({
  baseUrl,
  sku,
  operation,
  input,
  title,
  agentActions,
  keySource,
  apiKey,
  inputTab,
  footer,
}: RequestSnippetsProps) => {
  const [tab, setTab] = useState<SnippetTab>(inputTab ? 'input' : 'python');
  const snippets = buildSnippets({ baseUrl, operation, input, apiKey });
  const codeLang: SnippetLang = tab === 'input' ? 'python' : tab;
  const code = snippets[codeLang];
  const copyLabel = m.console_copy_language({ language: LANGS.find((l) => l.id === codeLang)?.label ?? '' });

  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-line bg-surface">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-line-2 px-4 py-2.5">
        <Tabs value={tab} onValueChange={(v) => setTab(v as SnippetTab)}>
          <TabsList variant="line" className="h-auto p-0">
            {inputTab ? (
              <TabsTrigger value="input" className="inline-flex items-center gap-1.5 px-2.5 py-1 text-13">
                <HugeiconsIcon icon={PencilEdit01Icon} strokeWidth={1.5} className="size-3.5 shrink-0" />
                {m.console_input_tab()}
              </TabsTrigger>
            ) : null}
            {LANGS.map((l) => (
              <TabsTrigger
                key={l.id}
                value={l.id}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-13"
              >
                <LangGlyph lang={l.id} />
                {l.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        {tab === 'input' ? null : <CopyButton value={code} label={copyLabel} />}
      </div>
      {tab === 'input' ? (
        <div className="grow overflow-auto max-h-130">{inputTab}</div>
      ) : (
        // ph-no-capture when the code inlines a plaintext key, so PostHog
        // autocapture and session replay never read it.
        <pre
          className={cn(
            'mono grow overflow-auto max-h-130 px-4 py-3 text-12 leading-relaxed text-text-2',
            apiKey && 'ph-no-capture',
          )}
        >
          <CodeHighlight code={code} lang={codeLang} variant="card" />
        </pre>
      )}
      {footer ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line-2 px-4 py-3">
          {footer}
        </div>
      ) : null}
      {/* Last row, under the run controls: handing the request to an agent is
          the secondary path, and it belongs to the request rather than to the
          tab being read, so it stays put as the tabs change. */}
      {agentActions ? (
        <SnippetActions sku={sku} title={title} apiKey={apiKey} keySource={keySource} />
      ) : null}
    </div>
  );
};
